import { Component, Host, h, Prop, JSX, Event, EventEmitter, State, Listen } from '@stencil/core'
import { container, ListingProduct, ProductService } from '@rdss/client'
import { DetailProduct } from '~/types'
import { store, trans } from '~/store'

@Component({
    tag: 'zr-add-to-cart-overlay',
    styleUrl: 'zr-add-to-cart-overlay.scss',
    shadow: true,
})

/**
 * ZooRoyal price component.
 */
export class ZrAddToCartOverlay {
    /**
     * Product Tile context.
     */
    @Prop() context = 'product-category-overlay'

    /**
     * Product ID.
     */
    @Prop() productId: string

    /**
     * Listing product representation.
     */
    @Prop() listingProduct: ListingProduct

    /**
     * Variants.
     */
    @Prop() variants: {
        sku: string
        stock: number
    }[]

    /**
     * Variants in cart label.
     */
    @Prop() variantsInCartLabel = 'Artikel versch. Varianten im Warenkorb.'

    /**
     * Product service.
     */
    @Prop() readonly productService = container.resolve(ProductService)

    /**
     * Is sponsored.
     */
    @Prop() isSponsored = false

    /**
     * Handle focus event.
     */
    @Event() addToCartOverlayClosed: EventEmitter

    /**
     * Product.
     */
    @State() private product: DetailProduct

    /**
     * Total variants cart quantity.
     */
    @State() private totalVariantsCartQuantity = 0

    /**
     * Handle focus event.
     */
    @Event() addToCartFocus: EventEmitter

    /**
     * Temp cart.
     */
    // private tempCart: RdssSimpleCart | null = null

    /**
     * Handle open click.
     */
    private handleOpenClick = () => {
        this.loadProduct()
    }

    /**
     * Handle focus event.
     */
    private handleFocus = () => {
        this.addToCartFocus.emit()
    }

    /**
     * Load Product by sku.
     */
    private loadProduct = async (sku = this.productId) => {
        await this.productService
            .findBySku(sku)
            .then(async ({ data }) => {
                this.product = data
            })
            .catch(error => console.error(ZrAddToCartOverlay.name, `Error while getting product ${sku}`, error))
    }

    /**
     * Checks if product is in cart.
     */
    getCartQuantity(sku: string): number {
        const item = store.state.cart?.items?.find(obj => obj.sku === sku)
        return item?.quantity || 0
    }

    /**
     * Get total variants cart quantity.
     */
    getTotalVariantsCartQuantity(): number {
        let total = 0
        this.variants?.forEach((variant) => {
            total += this.getCartQuantity(variant.sku)
        })
        return total
    }

    /**
     * Lifecycle method called before the component is rendered.
     */
    componentWillRender() {
        const total = this.getTotalVariantsCartQuantity()
        if (total !== this.totalVariantsCartQuantity) {
            this.totalVariantsCartQuantity = total
        }
    }

    /**
     * Add to cart clicked handler.
     */
    @Listen('handleQuantityChange')
    async addToCartClickedHandler() {
        setTimeout(() => {
            this.totalVariantsCartQuantity = this.getTotalVariantsCartQuantity()
        }, 1000)
    }

    /**
     * Renders the component.
     *
     * @returns {JSX.Element} The rendered component.
     */
    render(): JSX.Element {
        return (
            <Host>
                <zr-modal class={{ 'large': this.listingProduct?.isBiocide || this.listingProduct?.isVeterinary }}>
                    {this.product && (
                        <zr-product-variants
                            slot="zr-modal-content"
                            product={this.product}
                            listingProduct={this.listingProduct}
                            variants={this.variants}
                            isSponsored={this.isSponsored}
                            context={this.context}
                        />
                    ) || (
                        <div class="zr-modal-loading" slot="zr-modal-content">
                            <vx-loading-spinner/>
                        </div>
                    )}
                    <div slot="zr-modal-trigger">
                        <div class="zr-add-to-cart-overlay__quantity"
                            onClick={(ev: MouseEvent) => ev.stopPropagation()}>
                            {this.totalVariantsCartQuantity > 0 && (
                                <span>{this.totalVariantsCartQuantity} {this.variantsInCartLabel}</span>
                            )}
                        </div>
                        <button
                            class={{
                                'zr-add-to-cart-overlay__add': true,
                            }}
                            aria-label={`${trans(
                                'frontend/listing/addtocart.selectProductVariants',
                                'Auswahl Produktvarianten'
                            )} ${this.listingProduct?.name}`}
                            onClick={this.handleOpenClick}
                            onFocus={this.handleFocus}
                        >
                            <vx-icon
                                icon="add_shopping_cart"
                                alt="produkt hinzufügen"
                                invert={true}
                            ></vx-icon>
                        </button>
                    </div>
                </zr-modal>
            </Host>
        )
    }
}
